/**
 * Gate policy is "the rule for leaving this stage." Hardcoded UI map for the
 * POC — backend doesn't yet model per-stage gate config. When that lands, this
 * file shrinks to a thin lookup against `promotionDetail.gatePolicies`.
 *
 * Severity counts in evaluate(...) are issue counts, scoped to the rollup's
 * gate-relevant set (i.e., issues on disabled discounts are already excluded
 * by the caller — see lifecycleRollup.js).
 */

export const POLICIES = {
  STANDARD: "standard",
  STRICT: "strict",
  PERMISSIVE: "permissive",
};

// Lifecycle was expanded to three stages (see ppt-server migration 018).
// All three default to STANDARD until product confirms a stricter posture is
// needed. The dropped stages (internal_approval, client_approval, done) are
// intentionally absent.
const GATE_BY_STAGE_ID = {
  data_sync: POLICIES.STANDARD,
  sync_ready: POLICIES.STANDARD,
  portal_upload: POLICIES.STANDARD,
};

export const gatePolicyFor = (stageId) =>
  stageId ? (GATE_BY_STAGE_ID[stageId] ?? null) : null;

/**
 * Evaluate the gate. Returns the user-facing decision in three buckets:
 *   - "clean"    : allowed, no acknowledgment needed (one click advances)
 *   - "warnings" : allowed with required acknowledgment modal
 *   - "blocked"  : not allowed; modal explains why (read-only)
 */
export const evaluateGate = (policy, errors, warnings) => {
  if (policy == null) {
    return { mode: "blocked", allowed: false, reason: "Final stage." };
  }
  if (policy === POLICIES.PERMISSIVE) {
    return { mode: "clean", allowed: true };
  }
  if (policy === POLICIES.STRICT) {
    if (errors === 0 && warnings === 0) {
      return { mode: "clean", allowed: true };
    }
    return {
      mode: "blocked",
      allowed: false,
      reason:
        errors > 0 && warnings > 0
          ? `${errors} error${errors === 1 ? "" : "s"} and ${warnings} warning${warnings === 1 ? "" : "s"} must be resolved before advancing.`
          : errors > 0
            ? `${errors} error${errors === 1 ? "" : "s"} must be resolved before advancing.`
            : `${warnings} warning${warnings === 1 ? "" : "s"} must be resolved before advancing.`,
    };
  }
  // STANDARD
  if (errors > 0) {
    return {
      mode: "blocked",
      allowed: false,
      reason: `${errors} error${errors === 1 ? "" : "s"} must be resolved before advancing.`,
    };
  }
  if (warnings > 0) {
    return {
      mode: "warnings",
      allowed: true,
      requiresAck: true,
    };
  }
  return { mode: "clean", allowed: true };
};

export const policyExplanation = (policy) => {
  switch (policy) {
    case POLICIES.STRICT:
      return "Strict gate — all issues must be resolved before advancing.";
    case POLICIES.STANDARD:
      return "Standard gate — warnings are allowed with acknowledgment, errors block.";
    case POLICIES.PERMISSIVE:
      return "Permissive gate — any open issues are allowed.";
    default:
      return "Final stage — no further progression.";
  }
};
